import React from "react"

import classNames from "classnames"

import { useElementHeightStore } from "shared/lib/css/useElementHeightStore"
import { scrollWindowTo } from "shared/lib/scrollWindowTo"
import { useEvent } from "shared/lib/useEvent"
import { Anchor } from "shared/ui/link/Anchor"

import "./styles/_quick-nav.scss"

const QuickNavContext = React.createContext<{ activeHref?: string }>({})

interface RootProps {
  className?: string
  label?: string
  activeHref?: string
  children: React.ReactNode
}

const Root = React.memo(
  ({ className, label, activeHref, children }: RootProps) => {
    const context = React.useMemo(() => ({ activeHref }), [activeHref])

    return (
      <QuickNavContext.Provider value={context}>
        <nav
          data-testid="quick-nav"
          className={classNames("quick-nav", className)}
          aria-label={label}
        >
          {children}
        </nav>
      </QuickNavContext.Provider>
    )
  }
)

interface AnchorProps {
  href: string
  children: React.ReactNode
}

const QuickNavAnchor = React.memo(({ href, children }: AnchorProps) => {
  const { activeHref } = React.useContext(QuickNavContext)
  const headerHeight = useElementHeightStore("header-height")

  const handleClick = useEvent((e: React.MouseEvent) => {
    e.preventDefault()
    scrollWindowTo(href.replace("#", ""), {
      offsetY: headerHeight,
      scrollBehavior: "smooth"
    })
  })

  return (
    <Anchor
      className="quick-nav__anchor"
      data-active={href === activeHref ? "" : undefined}
      href={href}
      onClick={handleClick}
    >
      {children}
    </Anchor>
  )
})

export const QuickNav = {
  Root,
  Anchor: QuickNavAnchor
}
